import { Routes } from '@angular/router';
import { authGuard } from './authentication/guards/auth.guard';
import { LegalLayoutComponent } from './layouts/legal-layout/legal-layout.component';
import { WebsiteLayoutComponent } from './layouts/website-layout/website-layout.component';
import { ReviewRedirectGuard } from './shared/guards/review-redirect.guard';
import { routingVerificationGuard } from './shared/guards/routing-verification.guard';

export const routes: Routes = [
  {
    path: '',
    component: WebsiteLayoutComponent,
    children: [
      { path: '', loadComponent: () => import('./features/website/home/home.component').then((m) => m.HomeComponent), title: 'Joet' },
      { path: 'about', loadComponent: () => import('./features/website/about/about.component').then((m) => m.AboutComponent), title: 'Joet | About' },
      {
        path: 'category',
        loadComponent: () => import('./features/website/category/category.component').then((m) => m.VehicleCategoriesComponent),
        title: 'Joet | Category',
      },
      {
        path: 'fleets',
        loadComponent: () => import('./features/website/fleet/fleet.component').then((m) => m.OurFleetComponent),
        title: 'Joet | Our Fleets',
      },
      {
        // #1: a fleet's public profile (drivers, vehicles, reviews, booking a driver or the whole fleet).
        path: 'fleets/:id',
        loadComponent: () => import('./features/website/fleet-profile/fleet-profile.component').then((m) => m.FleetProfileComponent),
        title: 'Joet | Fleet',
      },
      {
        path: 'contact',
        loadComponent: () => import('./features/website/contact/contact.component').then((m) => m.ContactUsComponent),
        title: 'Joet | Contact Us',
      },
      {
        path: 'booking',
        loadComponent: () => import('./features/website/booking/booking.component').then((m) => m.WebBookingComponent),
        canActivate: [authGuard, routingVerificationGuard('user')],
        title: 'Joet | Booking',
      },
      {
        path: 'lax-booking',
        loadComponent: () => import('./features/website/lax-booking/lax-booking.component').then((m) => m.LaxBookingComponent),
        canActivate: [authGuard, routingVerificationGuard('user')],
        title: 'Joet | Lax Booking',
      },
    ],
  },
  {
    path: 'admin',
    loadComponent: () => import('./layouts/dashboard-layout/dashboard-layout.component').then((m) => m.DashboardLayoutComponent),
    canActivate: [authGuard, routingVerificationGuard('admin')],
    canActivateChild: [ReviewRedirectGuard],
    children: [
      {
        path: 'ratings/driver/:id',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'driver' },
        title: 'Joet | Driver Ratings',
      },
      {
        path: 'ratings/fleet/:id',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'fleet' },
        title: 'Joet | Fleet Ratings',
      },
      {
        path: 'ratings/customer/:id',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'customer' },
        title: 'Joet | Passenger Ratings',
      },
      {
        // Review #19: fleet descriptions waiting for approval.
        path: 'fleet-descriptions',
        loadComponent: () => import('./features/admin/fleet-descriptions/fleet-descriptions.component').then((m) => m.FleetDescriptionsComponent),
        title: 'Joet | Fleet Descriptions',
      },
      {
        // Review #37: fleets whose direct bookings were turned off after releasing trips.
        path: 'fleet-direct-bookings',
        loadComponent: () => import('./features/admin/fleet-direct-bookings/fleet-direct-bookings.component').then((m) => m.FleetDirectBookingsComponent),
        title: 'Joet | Fleet Direct Bookings',
      },
      {
        // #16/#1: new driver and fleet pictures waiting for approval (admin only; first match is the admin block).
        path: 'picture-reviews',
        loadComponent: () => import('./features/admin/picture-reviews/picture-reviews.component').then((m) => m.PictureReviewsComponent),
        title: 'Joet | Picture Approvals',
      },
      {
        // Admin: confirm or withdraw passengers', drivers' and fleets' email and phone verification.
        path: 'contact-verification',
        loadComponent: () => import('./features/admin/contact-verification/contact-verification.component').then((m) => m.ContactVerificationComponent),
        title: 'Joet | Email & Phone Verification',
      },
      {
        // #8: drivers asking to join a fleet.
        path: 'driver-requests',
        loadComponent: () => import('./features/fleet/driver-requests/driver-requests-list/driver-requests-list.component').then((m) => m.DriverRequestsListComponent),
        title: 'Joet | Driver Requests',
      },
      {
        path: 'driver-requests/:id',
        loadComponent: () => import('./features/fleet/driver-requests/driver-request-detail/driver-request-detail.component').then((m) => m.DriverRequestDetailComponent),
        title: 'Joet | Driver Request',
      },
      { path: '', loadComponent: () => import('./features/admin/dashboard/dashboard.component').then((m) => m.DashboardComponent), title: 'Joet | Dashboard' },
      {
        path: 'fleet/list',
        loadComponent: () => import('./features/admin/fleet/fleet-list/fleet-list.component').then((m) => m.FleetListComponent),
        title: 'Joet | Fleet List',
      },
      {
        path: 'fleet/add',
        loadComponent: () => import('./features/admin/fleet/fleet-add/fleet-add.component').then((m) => m.FleetAddComponent),
        title: 'Joet | Add New Fleet',
      },
      {
        path: 'fleet/view',
        loadComponent: () => import('./features/admin/fleet/fleet-view/fleet-view.component').then((m) => m.FleetViewComponent),
        title: 'Joet | View Fleet',
      },
      {
        path: 'fleet/view/:id',
        loadComponent: () => import('./features/admin/fleet/fleet-view/fleet-view.component').then((m) => m.FleetViewComponent),
        title: 'Joet | View Fleet',
      },
      {
        path: 'fleet/edit/:id',
        loadComponent: () => import('./features/admin/fleet/fleet-edit/fleet-edit.component').then((m) => m.FleetEditComponent),
        title: 'Joet | Edit Fleet',
      },
      {
        path: 'fleet/edit',
        loadComponent: () => import('./features/admin/fleet/fleet-edit/fleet-edit.component').then((m) => m.FleetEditComponent),
        title: 'Joet | Edit Fleet',
      },
      {
        path: 'vehicle-category/list',
        loadComponent: () => import('./features/admin/vehicle-category/vehicle-category-list/vehicle-category-list.component').then((m) => m.VehicleCategoryListComponent),
        title: 'Joet | Vehicle Category List',
      },
      {
        path: 'vehicle-category/add',
        loadComponent: () => import('./features/admin/vehicle-category/vehicle-category-add/vehicle-category-add.component').then((m) => m.VehicleCategoryAddComponent),
        title: 'Joet | Add Vehicle Category',
      },
      {
        path: 'vehicle-category/view/:id',
        loadComponent: () => import('./features/admin/vehicle-category/vehicle-category-view/vehicle-category-view.component').then((m) => m.VehicleCategoryViewComponent),
        title: 'Joet | View Vehicle Category',
      },
      {
        path: 'vehicle-category/edit/:id',
        loadComponent: () => import('./features/admin/vehicle-category/vehicle-category-edit/vehicle-category-edit.component').then((m) => m.VehicleCategoryEditComponent),
        title: 'Joet | Edit Vehicle Category',
      },
      {
        path: 'vehicle-category/sort',
        loadComponent: () => import('./features/admin/vehicle-category/vehicle-category-sorting/vehicle-category-sorting.component').then((m) => m.VehicleCategorySortingComponent),
        title: 'Joet | Sort Vehicle Category',
      },
      {
        path: 'customer/list',
        loadComponent: () => import('./features/admin/customer/customer-list/customer-list.component').then((m) => m.CustomerListComponent),
        title: 'Joet | Customer List',
      },
      {
        path: 'customer/trip-history',
        loadComponent: () => import('./features/admin/customer/customer-trip-history/customer-trip-history.component').then((m) => m.CustomerTripHistoryComponent),
        title: 'Joet | Customer Trip History',
      },
      {
        path: 'customer/view',
        loadComponent: () => import('./features/admin/customer/customer-view/customer-view.component').then((m) => m.CustomerViewComponent),
        title: 'Joet | Customer View',
      },
      {
        path: 'customer/request',
        loadComponent: () => import('./features/admin/customer/customer-request/customer-request.component').then((m) => m.CustomerRequestComponent),
        title: 'Joet | Customer Requests',
      },
      {
        path: 'driver/list',
        loadComponent: () => import('./features/admin/driver/driver-list/driver-list.component').then((m) => m.DriverListComponent),
        title: 'Joet | Driver List',
      },
      {
        path: 'driver/view',
        loadComponent: () => import('./features/admin/driver/driver-view/driver-view.component').then((m) => m.DriverViewComponent),
        title: 'Joet | Driver View',
      },
      {
        path: 'driver/view/:id',
        loadComponent: () => import('./features/admin/driver/driver-view/driver-view.component').then((m) => m.DriverViewComponent),
        title: 'Joet | Driver View',
      },
      {
        path: 'driver/edit',
        loadComponent: () => import('./features/admin/driver/driver-edit/driver-edit.component').then((m) => m.DriverEditComponent),
        title: 'Joet | Driver Edit',
      },
      {
        path: 'driver/edit/:id',
        loadComponent: () => import('./features/admin/driver/driver-edit/driver-edit.component').then((m) => m.DriverEditComponent),
        title: 'Joet | Driver Edit',
      },
      {
        path: 'driver/add',
        loadComponent: () => import('./features/admin/driver/driver-add/driver-add.component').then((m) => m.DriverAddComponent),
        title: 'Joet | Driver Add',
      },
      {
        path: 'vehicle/list',
        loadComponent: () => import('./features/admin/vehicle/vehicle-list/vehicle-list.component').then((m) => m.VehicleListComponent),
        title: 'Joet | Vehicle List',
      },
      {
        path: 'vehicle/add',
        loadComponent: () => import('./features/admin/vehicle/vehicle-add/vehicle-add.component').then((m) => m.VehicleAddComponent),
        title: 'Joet | Add Vehicle',
      },
      {
        path: 'vehicle/edit/:id',
        loadComponent: () => import('./features/admin/vehicle/vehicle-update/vehicle-update.component').then((m) => m.VehicleUpdateComponent),
        title: 'Joet | Edit Vehicle',
      },
      {
        path: 'vehicle/view/:id',
        loadComponent: () => import('./features/admin/vehicle/vehicle-view/vehicle-view.component').then((m) => m.VehicleViewComponent),
        title: 'Joet | View Vehicle',
      },
      {
        path: 'reservations/list',
        loadComponent: () => import('./features/admin/reservations/reservations-list/reservations-list.component').then((m) => m.ReservationsListComponent),
        title: 'Joet | Assigned Vehicles',
      },
      {
        path: 'user-queries/list',
        loadComponent: () => import('./features/admin/user-queries/user-queries-list/user-queries-list.component').then((m) => m.UserQueriesListComponent),
        title: 'Joet | User Queries',
      },
      {
        path: 'newsletter/list',
        loadComponent: () => import('./features/admin/newsletter/newsletter-list/newsletter-list.component').then((m) => m.NewsletterListComponent),
        title: 'Joet | Newsletter',
      },
      {
        path: 'booking/new-requests',
        loadComponent: () => import('./features/admin/booking/new-request/new-request.component').then((m) => m.NewRequestComponent),
        title: 'Joet | New Booking Requests',
      },
      {
        path: 'booking/progress',
        loadComponent: () => import('./features/admin/booking/progress-request/progress-request.component').then((m) => m.ProgressRequestComponent),
        title: 'Joet |Progress Requests',
      },
      {
        path: 'booking/request-history',
        loadComponent: () => import('./features/admin/booking/request-history/request-history.component').then((m) => m.RequestHistoryComponent),
        title: 'Joet | Request History',
      },
      {
        path: 'booking/attention',
        loadComponent: () => import('./features/admin/booking/require-attention/require-attention.component').then((m) => m.RequireAttentionComponent),
        title: 'Joet | Require Attention',
      },
      {
        path: 'booking/cacnelAndRefund',
        loadComponent: () => import('./features/admin/booking/cancel-and-refund-booking/cancel-and-refund-booking.component').then((m) => m.CancelAndRefundBookingComponent),
        title: 'Joet | Cancel And Refund',
      },
      {
        path: 'booking/cleaning-fees',
        loadComponent: () => import('./features/admin/booking/cleaning-fees/cleaning-fees.component').then((m) => m.CleaningFeesComponent),
        title: 'Joet | Cleaning Fees',
      },
      {
        path: 'earning/payouts',
        loadComponent: () => import('./features/admin/earning/payouts/payouts.component').then((m) => m.AdminPayoutsComponent),
        title: 'Joet | Payouts',
      },
      {
        path: 'settings/business',
        loadComponent: () => import('./features/admin/settings/business-settings/business-settings.component').then((m) => m.BusinessSettingsComponent),
        title: 'Joet | Business Settings',
      },
      {
        // Review #9: Driver Fleet Transfer Terms & Conditions.
        path: 'settings/fleet-join-terms',
        loadComponent: () => import('./features/admin/settings/fleet-join-terms/fleet-join-terms.component').then((m) => m.FleetJoinTermsComponent),
        title: 'Joet | Fleet Join Terms',
      },
      {
        // #45: help tickets.
        path: 'support/tickets',
        loadComponent: () => import('./features/admin/support/support-ticket-list/support-ticket-list.component').then((m) => m.SupportTicketListComponent),
        title: 'Joet | Help Tickets',
      },
      {
        path: 'support/tickets/:id',
        loadComponent: () => import('./features/admin/support/support-ticket-manage/support-ticket-manage.component').then((m) => m.SupportTicketManageComponent),
        title: 'Joet | Help Ticket',
      },
      {
        // #30: promo codes.
        path: 'promo-codes',
        loadComponent: () => import('./features/admin/promo-codes/promo-code-list/promo-code-list.component').then((m) => m.PromoCodeListComponent),
        title: 'Joet | Promo Codes',
      },
      {
        path: 'promo-codes/add',
        loadComponent: () => import('./features/admin/promo-codes/promo-code-form/promo-code-form.component').then((m) => m.PromoCodeFormComponent),
        title: 'Joet | Add Promo Code',
      },
      {
        path: 'promo-codes/edit/:id',
        loadComponent: () => import('./features/admin/promo-codes/promo-code-form/promo-code-form.component').then((m) => m.PromoCodeFormComponent),
        title: 'Joet | Edit Promo Code',
      },
      {
        path: 'booking/submit-review/:id',
        loadComponent: () => import('./features/admin/booking/submit-review/submit-review.component').then((m) => m.SubmitReviewComponent),
        title: 'Joet | Submit Review',
      },
      {
        path: 'earning/previous-week',
        loadComponent: () => import('./features/admin/earning/previous-week/previous-week.component').then((m) => m.PreviousWeekEarningsComponent),
        title: 'Joet | Previous Week Earnings',
      },
      {
        path: 'earning/current-week',
        loadComponent: () => import('./features/admin/earning/current-week/current-week.component').then((m) => m.CurrentWeekEarningsComponent),
        title: 'Joet | Current Week Earnings',
      },
      {
        path: 'earning/fleet-payouts',
        loadComponent: () => import('./features/admin/earning/fleet-payout/fleet-payout.component').then((m) => m.FleetPayoutComponent),
        title: 'Joet | Fleet Payout',
      },
      {
        path: 'user-profile/edit',
        loadComponent: () => import('./shared/components/update-profile/update-profile.component').then((m) => m.UpdateProfileComponent),
        title: 'Joet | User Profile Edit',
      },
      {
        path: 'update-password',
        loadComponent: () => import('./features/auth/update-password/update-password.component').then((m) => m.UpdatePasswordComponent),
        title: 'Joet | Update Password',
      },
    ],
  },
  {
    path: 'user',
    loadComponent: () => import('./layouts/user-layout/user-layout.component').then((m) => m.UserDashboardLayoutComponent),
    canActivate: [authGuard, routingVerificationGuard('user')],
    canActivateChild: [ReviewRedirectGuard],

    children: [
      {
        path: 'ratings',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'customer' },
        title: 'Joet | My Ratings',
      },
      {
        path: '',
        loadComponent: () => import('./features/user/dashboard/dashboard.component').then((m) => m.UserDashboardComponent),
        title: 'Joet | User Dashboard',
      },
      {
        path: 'booking',
        loadComponent: () => import('./features/user/booking/booking.component').then((m) => m.UserBookingComponent),
        title: 'Joet | User Booking',
      },
      {
        path: 'lax-booking',
        loadComponent: () => import('./features/user/lax-booking/lax-booking.component').then((m) => m.UserLaxBookingComponent),
        title: 'Joet | User Lax Booking',
      },
      {
        path: 'booking/new-requests',
        loadComponent: () => import('./features/admin/booking/new-request/new-request.component').then((m) => m.NewRequestComponent),
        title: 'Joet | New Booking Requests',
      },
      {
        path: 'booking/progress',
        loadComponent: () => import('./features/admin/booking/progress-request/progress-request.component').then((m) => m.ProgressRequestComponent),
        title: 'Joet |Progress Requests',
      },
      {
        path: 'booking/request-history',
        loadComponent: () => import('./features/admin/booking/request-history/request-history.component').then((m) => m.RequestHistoryComponent),
        title: 'Joet | Request History',
      },
      {
        path: 'booking/submit-review/:id',
        loadComponent: () => import('./features/admin/booking/submit-review/submit-review.component').then((m) => m.SubmitReviewComponent),
        title: 'Joet | Submit Review',
      },
      {
        path: 'user-profile/edit',
        loadComponent: () => import('./shared/components/update-profile/update-profile.component').then((m) => m.UpdateProfileComponent),
        title: 'Joet | User Profile Edit',
      },
      {
        path: 'update-password',
        loadComponent: () => import('./features/auth/update-password/update-password.component').then((m) => m.UpdatePasswordComponent),
        title: 'Joet | Update Password',
      },
      {
        path: 'give-tip/:id',
        loadComponent: () => import('./features/user/give-tip/give-tip.component').then((m) => m.GiveTipComponent),
        title: 'Joet | Give Tip',
      },
      {
        path: 'cleaning-fee/:id',
        loadComponent: () => import('./features/user/cleaning-fee-payment/cleaning-fee-payment.component').then((m) => m.CleaningFeePaymentComponent),
        title: 'Joet | Cleaning Fee',
      },
      {
        // #45: help center (assistant first, then tickets).
        path: 'help',
        loadComponent: () => import('./shared/components/help-center/help-center-page/help-center-page.component').then((m) => m.HelpCenterPageComponent),
        title: 'Joet | Help Center',
      },
      {
        path: 'help/tickets/:id',
        loadComponent: () => import('./shared/components/help-center/support-ticket-detail-page/support-ticket-detail-page.component').then((m) => m.SupportTicketDetailPageComponent),
        title: 'Joet | Help Ticket',
      },
    ],
  },
  {
    path: 'fleet',
    loadComponent: () => import('./layouts/fleet-layout/fleet-layout.component').then((m) => m.FleetDashboardLayoutComponent),
    canActivate: [authGuard, routingVerificationGuard('fleet')],
    canActivateChild: [ReviewRedirectGuard],
    children: [
      {
        // #8: drivers asking to join a fleet.
        path: 'driver-requests',
        loadComponent: () => import('./features/fleet/driver-requests/driver-requests-list/driver-requests-list.component').then((m) => m.DriverRequestsListComponent),
        title: 'Joet | Driver Requests',
      },
      {
        path: 'driver-requests/:id',
        loadComponent: () => import('./features/fleet/driver-requests/driver-request-detail/driver-request-detail.component').then((m) => m.DriverRequestDetailComponent),
        title: 'Joet | Driver Request',
      },
      {
        // #45: help center (assistant first, then tickets).
        path: 'help',
        loadComponent: () => import('./shared/components/help-center/help-center-page/help-center-page.component').then((m) => m.HelpCenterPageComponent),
        title: 'Joet | Help Center',
      },
      {
        path: 'help/tickets/:id',
        loadComponent: () => import('./shared/components/help-center/support-ticket-detail-page/support-ticket-detail-page.component').then((m) => m.SupportTicketDetailPageComponent),
        title: 'Joet | Help Ticket',
      },
      {
        path: 'ratings',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'fleet' },
        title: 'Joet | Fleet Ratings',
      },
      {
        path: 'ratings/driver/:id',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'driver' },
        title: 'Joet | Driver Ratings',
      },
      {
        path: '',
        loadComponent: () => import('./features/fleet/fleet-dashboard/fleet-dashboard.component').then((m) => m.FleetDashboardComponent),
        title: 'Joet | Fleet Dashboard',
      },
      {
        path: 'fleet/view',
        loadComponent: () => import('./features/admin/fleet/fleet-view/fleet-view.component').then((m) => m.FleetViewComponent),
        title: 'Joet | View Fleet',
      },
      {
        path: 'fleet/view/:id',
        loadComponent: () => import('./features/admin/fleet/fleet-view/fleet-view.component').then((m) => m.FleetViewComponent),
        title: 'Joet | View Fleet',
      },
      {
        path: 'fleet/edit/:id',
        loadComponent: () => import('./features/admin/fleet/fleet-edit/fleet-edit.component').then((m) => m.FleetEditComponent),
        title: 'Joet | Edit Fleet',
      },
      {
        path: 'fleet/edit',
        loadComponent: () => import('./features/admin/fleet/fleet-edit/fleet-edit.component').then((m) => m.FleetEditComponent),
        title: 'Joet | Edit Fleet',
      },
      {
        path: 'driver/list',
        loadComponent: () => import('./features/admin/driver/driver-list/driver-list.component').then((m) => m.DriverListComponent),
        title: 'Joet | Driver List',
      },
      {
        path: 'driver/view',
        loadComponent: () => import('./features/admin/driver/driver-view/driver-view.component').then((m) => m.DriverViewComponent),
        title: 'Joet | Driver View',
      },
      {
        path: 'driver/view/:id',
        loadComponent: () => import('./features/admin/driver/driver-view/driver-view.component').then((m) => m.DriverViewComponent),
        title: 'Joet | Driver View',
      },
      {
        path: 'driver/edit',
        loadComponent: () => import('./features/admin/driver/driver-edit/driver-edit.component').then((m) => m.DriverEditComponent),
        title: 'Joet | Driver Edit',
      },
      {
        path: 'driver/edit/:id',
        loadComponent: () => import('./features/admin/driver/driver-edit/driver-edit.component').then((m) => m.DriverEditComponent),
        title: 'Joet | Driver Edit',
      },
      {
        path: 'driver/add',
        loadComponent: () => import('./features/admin/driver/driver-add/driver-add.component').then((m) => m.DriverAddComponent),
        title: 'Joet | Driver Add',
      },
      {
        path: 'vehicle/list',
        loadComponent: () => import('./features/admin/vehicle/vehicle-list/vehicle-list.component').then((m) => m.VehicleListComponent),
        title: 'Joet | Vehicle List',
      },
      {
        path: 'vehicle/add',
        loadComponent: () => import('./features/admin/vehicle/vehicle-add/vehicle-add.component').then((m) => m.VehicleAddComponent),
        title: 'Joet | Add Vehicle',
      },
      {
        path: 'vehicle/edit/:id',
        loadComponent: () => import('./features/admin/vehicle/vehicle-update/vehicle-update.component').then((m) => m.VehicleUpdateComponent),
        title: 'Joet | Edit Vehicle',
      },
      {
        path: 'vehicle/view/:id',
        loadComponent: () => import('./features/admin/vehicle/vehicle-view/vehicle-view.component').then((m) => m.VehicleViewComponent),
        title: 'Joet | View Vehicle',
      },
      {
        path: 'reservations/list',
        loadComponent: () => import('./features/admin/reservations/reservations-list/reservations-list.component').then((m) => m.ReservationsListComponent),
        title: 'Joet | Assigned Vehicles',
      },
      {
        path: 'reservations/add',
        loadComponent: () => import('./features/admin/reservations/reservation-add/reservation-add.component').then((m) => m.ReservationAddComponent),
        title: 'Joet | Assign Vehicle',
      },
      {
        path: 'earning/previous-week',
        loadComponent: () => import('./features/admin/earning/previous-week/previous-week.component').then((m) => m.PreviousWeekEarningsComponent),
        title: 'Joet | Previous Week Earnings',
      },
      {
        path: 'earning/current-week',
        loadComponent: () => import('./features/admin/earning/current-week/current-week.component').then((m) => m.CurrentWeekEarningsComponent),
        title: 'Joet | Current Week Earnings',
      },
      {
        path: 'earning/fleet-payouts',
        loadComponent: () => import('./features/admin/earning/fleet-payout/fleet-payout.component').then((m) => m.FleetPayoutComponent),
        title: 'Joet | Fleet Payout',
      },
      {
        path: 'earning/payouts',
        loadComponent: () => import('./features/fleet/earning/fleet-payouts/fleet-payouts.component').then((m) => m.FleetPayoutsComponent),
        title: 'Joet | Payouts',
      },
      {
        path: 'earning/payout-setup',
        loadComponent: () => import('./features/fleet/earning/payout-setup/payout-setup.component').then((m) => m.PayoutSetupComponent),
        title: 'Joet | Payout Setup',
      },
      {
        path: 'booking/cleaning-fees',
        loadComponent: () => import('./features/admin/booking/cleaning-fees/cleaning-fees.component').then((m) => m.CleaningFeesComponent),
        title: 'Joet | Cleaning Fees',
      },
      {
        path: 'booking/new-requests',
        loadComponent: () => import('./features/admin/booking/new-request/new-request.component').then((m) => m.NewRequestComponent),
        title: 'Joet | New Booking Requests',
      },
      {
        path: 'booking/progress',
        loadComponent: () => import('./features/admin/booking/progress-request/progress-request.component').then((m) => m.ProgressRequestComponent),
        title: 'Joet |Progress Requests',
      },
      {
        path: 'booking/request-history',
        loadComponent: () => import('./features/admin/booking/request-history/request-history.component').then((m) => m.RequestHistoryComponent),
        title: 'Joet | Request History',
      },
      {
        path: 'today-history',
        loadComponent: () => import('./features/fleet/fleet-today-history/fleet-today-history.component').then((m) => m.FleetTodayHistoryComponent),
        title: 'Joet | Today History',
      },
      {
        path: 'user-profile/edit',
        loadComponent: () => import('./shared/components/update-profile/update-profile.component').then((m) => m.UpdateProfileComponent),
        title: 'Joet | User Profile Edit',
      },
      {
        path: 'update-password',
        loadComponent: () => import('./features/auth/update-password/update-password.component').then((m) => m.UpdatePasswordComponent),
        title: 'Joet | Update Password',
      },
    ],
  },
  {
    path: 'driver',
    loadComponent: () => import('./layouts/driver-layout/driver-layout.component').then((m) => m.DriverDashboardLayoutComponent),
    canActivate: [authGuard, routingVerificationGuard('driver')],
    canActivateChild: [ReviewRedirectGuard],
    children: [
      {
        path: 'ratings',
        loadComponent: () => import('./shared/components/ratings-page/ratings-page.component').then((m) => m.RatingsPageComponent),
        data: { subject: 'driver' },
        title: 'Joet | My Ratings',
      },
      {
        path: '',
        loadComponent: () => import('./features/driver/driver-dashboard/driver-dashboard.component').then((m) => m.DriverDashboardComponent),
        title: 'Joet | Driver Dashboard',
      },
      {
        path: 'view-profile',
        loadComponent: () => import('./features/driver/driver-profile/driver-profile/driver-profile.component').then((m) => m.DriverProfileComponent),
        title: 'Joet | Driver Profile',
      },
      {
        path: 'update-profile',
        loadComponent: () => import('./features/driver/driver-profile/update-profile/update-profile.component').then((m) => m.UpdateDriverProfileComponent),
        title: 'Joet | Update Driver Profile',
      },
      {
        path: 'vehicle/list',
        loadComponent: () => import('./features/driver/vehicle/vehicle-list/vehicle-list.component').then((m) => m.DriverVehicleListComponent),
        title: 'Joet | Vehicle List',
      },
      {
        path: 'reservations/list',
        loadComponent: () => import('./features/admin/reservations/reservations-list/reservations-list.component').then((m) => m.ReservationsListComponent),
        title: 'Joet | Assigned Vehicles',
      },
      {
        path: 'categories',
        loadComponent: () => import('./features/driver/my-categories/my-categories.component').then((m) => m.MyCategoriesComponent),
        title: 'Joet | My Trip Categories',
      },
      {
        // #8/#19: current fleet, join or move to a fleet.
        path: 'fleet',
        loadComponent: () => import('./features/driver/my-fleet/my-fleet.component').then((m) => m.MyFleetComponent),
        title: 'Joet | My Fleet',
      },
      {
        // #34: driver license and background check.
        path: 'verification',
        loadComponent: () => import('./features/driver/verification/verification.component').then((m) => m.DriverVerificationComponent),
        title: 'Joet | Documents & Background Check',
      },
      {
        // #29/#45: always available, also while the account isn't active.
        path: 'help',
        loadComponent: () => import('./shared/components/help-center/help-center-page/help-center-page.component').then((m) => m.HelpCenterPageComponent),
        title: 'Joet | Help Center',
      },
      {
        path: 'help/tickets/:id',
        loadComponent: () => import('./shared/components/help-center/support-ticket-detail-page/support-ticket-detail-page.component').then((m) => m.SupportTicketDetailPageComponent),
        title: 'Joet | Help Ticket',
      },
      {
        path: 'go-history/list',
        loadComponent: () => import('./features/driver/ready-to-go-history/ready-to-go-history-list/ready-to-go-history-list.component').then((m) => m.ReadyToGoHistoryComponent),
        title: 'Joet | Ready To Go History',
      },
      {
        path: 'earning/current-week',
        loadComponent: () => import('./features/driver/earning/current-week/current-week.component').then((m) => m.DriverCurrentWeekEarningsComponent),
        title: 'Joet | Current Week Earnings',
      },
      {
        path: 'earning/previous-week',
        loadComponent: () => import('./features/driver/earning/previous-week/previous-week.component').then((m) => m.DriverPreviousWeekEarningsComponent),
        title: 'Joet | Previous Week Earnings',
      },
      {
        path: 'booking/new-requests',
        loadComponent: () => import('./features/admin/booking/new-request/new-request.component').then((m) => m.NewRequestComponent),
        title: 'Joet | New Booking Requests',
      },
      {
        path: 'booking/progress',
        loadComponent: () => import('./features/admin/booking/progress-request/progress-request.component').then((m) => m.ProgressRequestComponent),
        title: 'Joet |Progress Requests',
      },
      {
        path: 'booking/request-history',
        loadComponent: () => import('./features/admin/booking/request-history/request-history.component').then((m) => m.RequestHistoryComponent),
        title: 'Joet | Request History',
      },
      {
        path: 'today-history',
        loadComponent: () => import('./features/fleet/fleet-today-history/fleet-today-history.component').then((m) => m.FleetTodayHistoryComponent),
        title: 'Joet | Today History',
      },
      {
        path: 'driver/view',
        loadComponent: () => import('./features/admin/driver/driver-view/driver-view.component').then((m) => m.DriverViewComponent),
        title: 'Joet | Driver View',
      },

      {
        path: 'driver/edit',
        loadComponent: () => import('./features/admin/driver/driver-edit/driver-edit.component').then((m) => m.DriverEditComponent),
        title: 'Joet | Driver Edit',
      },
      {
        path: 'booking/submit-review/:id',
        loadComponent: () => import('./features/admin/booking/submit-review/submit-review.component').then((m) => m.SubmitReviewComponent),
        title: 'Joet | Submit Review',
      },
      {
        path: 'user-profile/edit',
        loadComponent: () => import('./shared/components/update-profile/update-profile.component').then((m) => m.UpdateProfileComponent),
        title: 'Joet | User Profile Edit',
      },
      {
        path: 'update-password',
        loadComponent: () => import('./features/auth/update-password/update-password.component').then((m) => m.UpdatePasswordComponent),
        title: 'Joet | Update Password',
      },
    ],
  },
  {
    path: 'legal',
    component: LegalLayoutComponent,
    children: [
      {
        path: 'terms',
        loadComponent: () => import('./features/legal/terms/terms.component').then((m) => m.TermsComponent),
        title: 'Joet | Terms of Service',
      },
      {
        path: 'policy',
        loadComponent: () => import('./features/legal/policy/policy.component').then((m) => m.PolicyComponent),
        title: 'Joet | Privacy Policy',
      },
      {
        path: 'fleet-terms',
        loadComponent: () => import('./features/legal/fleet-terms/fleet-terms.component').then((m) => m.FleetTermsComponent),
        title: 'Joet | Fleet Terms',
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () => import('./features/auth/login/login.component').then((m) => m.LoginComponent),
    title: 'Login | Joet',
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./features/auth/forget-password/forget-password.component').then((m) => m.ForgetPassword),
    title: 'Forgot Password | Joet',
  },
  {
    // Link from the "reset your password" email.
    path: 'reset-password',
    loadComponent: () => import('./features/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
    data: { purpose: 'reset' },
    title: 'Reset Password | Joet',
  },
  {
    // Link from the "set up your account" email sent when an admin or fleet creates the account.
    path: 'set-password',
    loadComponent: () => import('./features/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
    data: { purpose: 'setup' },
    title: 'Set Your Password | Joet',
  },
    {
    path: 'delete-account',
    loadComponent: () => import('./features/auth/delete-account/delete-account.component').then((m) => m.DeleteAccountComponent),
    title: 'Delete Account | Joet',
  },
  {
    path: 'forgot-primary-info',
    loadComponent: () => import('./features/auth/forgot-primary-info/forgot-primary-info.component').then((m) => m.ForgetPrimaryInfo),
    title: 'Forgot Primary Info | Joet',
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./features/auth/sign-up/sign-up.component').then((m) => m.SignUpComponent),
    title: 'Sign Up | Joet',
  },
  {
    // Unknown links (old emails, typos) go to the home page instead of a blank screen.
    path: '**',
    redirectTo: '',
  },
];
